import {
  BaseEntityDTO,
  BranchDTO,
  BranchYearRoundDTO,
  CriteriaBranchYearDTO,
  IBaseEntityDTO,
  IBranchDTO,
  IBranchYearRoundDTO,
  ITeamCriteriaDTO,
  IYearDTO,
  TeamCriteriaDTO,
  YearDTO,
} from './';
import { convertToDateWithLocale, formatDisplayDate, formatDateFieldInput } from '@/helpers';

export interface IBranchYearDTO extends IBaseEntityDTO {
  branch?: IBranchDTO;
  launch_location: string;
  launch_address: string;
  friday_block1?: Date;
  friday_block2?: Date;
  saturday?: Date;
  afterparty_loc: string;
  afterparty_address: string;
  afterparty_time?: Date;
  year?: IYearDTO;
  teamCriteria: ITeamCriteriaDTO[];
  max_teams: number;
  team_count: number;
  branchYearCriterias: CriteriaBranchYearDTO[];
  criteria_selection_start?: Date;
  criteria_selection_end?: Date;
  team_select: boolean;

  isFull(): boolean;
  percentLeft(): number;
  numberLeft(): number;
  fieldInputFridayBlock1: string;
  fieldInputFridayBlock2: string;
  fieldInputSaturday: string;
  fieldInputAfterPartyTime: string;
  fieldInputCriteriaStartDate: string;
  fieldInputCriteriaEndDate: string;
  displayFridayBlock1: string;
  displayFridayBlock2: string;
  displayCriteriaStartDate: string;
  displayCriteriaEndDate: string;
  displaySaturday: string;
  displayAfterPartyTime: string;
  rounds: IBranchYearRoundDTO[];
}

export class BranchYearDTO extends BaseEntityDTO implements IBranchYearDTO {
  branch?: IBranchDTO;
  launch_location: string;
  launch_address: string;
  friday_block1?: Date;
  friday_block2?: Date;
  saturday?: Date;
  afterparty_loc: string;
  afterparty_address: string;
  afterparty_time?: Date;
  year?: IYearDTO;
  max_teams: number;
  team_count: number;
  teamCriteria: ITeamCriteriaDTO[];
  branchYearCriterias: CriteriaBranchYearDTO[];
  criteria_selection_start?: Date;
  criteria_selection_end?: Date;
  team_select: boolean;
  rounds: IBranchYearRoundDTO[];

  constructor(data?: Partial<BranchYearDTO>) {
    super();
    if (data) {
      Object.assign(this, data);
    }
    this.branch = data?.branch ? new BranchDTO(data.branch) : new BranchDTO();
    this.launch_address = data?.launch_address ?? '';
    this.launch_location = data?.launch_location ?? '';
    this.friday_block1 = data?.friday_block1 ?? undefined;
    this.friday_block2 = data?.friday_block2 ?? undefined;
    this.criteria_selection_start = data?.criteria_selection_start ?? undefined;
    this.criteria_selection_end = data?.criteria_selection_end ?? undefined;
    this.saturday = data?.saturday ?? undefined;
    this.afterparty_address = data?.afterparty_address ?? '';
    this.afterparty_loc = data?.afterparty_loc ?? '';
    this.afterparty_time = data?.afterparty_time ?? undefined;
    this.teamCriteria = data?.teamCriteria
      ? data.teamCriteria.map((crit) => new TeamCriteriaDTO(crit))
      : [];
    this.year = data?.year ? new YearDTO(data.year) : new YearDTO();
    this.max_teams = data?.max_teams ?? -1;
    this.team_count = data?.team_count ?? 0;
    this.branchYearCriterias = data?.branchYearCriterias
      ? data.branchYearCriterias.map((byc) => new CriteriaBranchYearDTO(byc))
      : [];
    this.team_select = data?.team_select ?? false;
    this.rounds = data?.rounds ? data.rounds.map((round) => new BranchYearRoundDTO(round)) : [];
  }

  isFull(): boolean {
    return this.team_count >= this.max_teams && this.max_teams > 0;
  }
  percentLeft(): number {
    return this.max_teams > 0 ? (1 - (this.max_teams - this.team_count) / this.max_teams) * 100 : 0;
  }
  numberLeft(): number {
    return this.max_teams > 0 ? this.max_teams - this.team_count : Infinity;
  }

  /** EXTENDED Properties **/
  get fieldInputFridayBlock1(): string {
    return this.friday_block1 ? formatDateFieldInput(this.friday_block1, true) : '';
  }

  set fieldInputFridayBlock1(value: string) {
    this.friday_block1 = convertToDateWithLocale(value, true);
  }

  get fieldInputFridayBlock2(): string {
    return this.friday_block2 ? formatDateFieldInput(this.friday_block1, true) : '';
  }

  set fieldInputFridayBlock2(value: string) {
    this.friday_block2 = convertToDateWithLocale(value, true);
  }
  get fieldInputCriteriaStartDate(): string {
    return this.criteria_selection_start
      ? formatDateFieldInput(this.criteria_selection_start, true)
      : '';
  }
  set fieldInputCriteriaStartDate(value: string) {
    this.criteria_selection_start = convertToDateWithLocale(value, true);
  }

  get fieldInputCriteriaEndDate(): string {
    return this.criteria_selection_end
      ? formatDateFieldInput(this.criteria_selection_end, true)
      : '';
  }
  set fieldInputCriteriaEndDate(value: string) {
    this.criteria_selection_end = convertToDateWithLocale(value, true);
  }

  get fieldInputSaturday(): string {
    return this.saturday ? formatDateFieldInput(this.saturday, true) : '';
  }

  set fieldInputSaturday(value: string) {
    this.saturday = convertToDateWithLocale(value, true);
  }

  get fieldInputAfterPartyTime(): string {
    return this.afterparty_time ? formatDateFieldInput(this.afterparty_time, true) : '';
  }

  set fieldInputAfterPartyTime(value: string) {
    this.afterparty_time = convertToDateWithLocale(value, true);
  }

  get displayFridayBlock1(): string {
    return this.friday_block1 ? formatDisplayDate(this.friday_block1, true) : '';
  }

  get displayFridayBlock2(): string {
    return this.friday_block2 ? formatDisplayDate(this.friday_block2, true) : '';
  }

  get displaySaturday(): string {
    return this.saturday ? formatDisplayDate(this.saturday, true) : '';
  }

  get displayAfterPartyTime(): string {
    return this.afterparty_time ? formatDisplayDate(this.afterparty_time, true) : '';
  }

  get displayCriteriaStartDate(): string {
    return this.criteria_selection_start
      ? formatDisplayDate(this.criteria_selection_start, true)
      : '';
  }

  get displayCriteriaEndDate(): string {
    return this.criteria_selection_end ? formatDisplayDate(this.criteria_selection_end, true) : '';
  }
}
